// src/pages/Blog.jsx
import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState('All Posts');
  
  const categories = [
    'All Posts',
    'Technology',
    'Business',
    'Digital Services',
    'News & Updates'
  ];

  const blogPosts = [
    {
      title: "The Future of Digital Payments in Ghana",
      category: "Technology",
      excerpt: "Exploring how mobile money and digital wallets are transforming financial inclusion...",
      author: "Sarah Johnson",
      date: "Feb 10, 2025",
      readTime: "5 min read",
      image: "https://placehold.co/600x400/FF6B2C/FFFFFF?text=Digital+Payments"
    },
    {
      title: "10 Ways to Optimize Your Business Communication",
      category: "Business",
      excerpt: "Discover proven strategies to enhance internal and external communication...",
      author: "Michael Carter",
      date: "Feb 8, 2025",
      readTime: "7 min read",
      image: "https://placehold.co/600x400/1E3A5F/FFFFFF?text=Business+Communication"
    },
    {
      title: "Understanding USSD Technology and Its Benefits",
      category: "Digital Services",
      excerpt: "A comprehensive guide to USSD self-service platforms and implementation...",
      author: "Emmanuel Taylor",
      date: "Feb 5, 2025",
      readTime: "6 min read",
      image: "https://placehold.co/600x400/FF6B2C/FFFFFF?text=USSD+Technology"
    },
    {
      title: "Ghana's Tech Ecosystem: 2025 Growth Report",
      category: "News & Updates",
      excerpt: "Latest statistics and trends shaping the technology landscape...",
      author: "Ama Osei",
      date: "Feb 3, 2025",
      readTime: "8 min read",
      image: "https://placehold.co/600x400/1E3A5F/FFFFFF?text=Tech+Ecosystem"
    },
    {
      title: "SMS Marketing Best Practices for SMEs",
      category: "Business",
      excerpt: "Learn how to craft effective SMS campaigns that drive engagement...",
      author: "Kwame Mensah",
      date: "Jan 30, 2025",
      readTime: "5 min read",
      image: "https://placehold.co/600x400/FF6B2C/FFFFFF?text=SMS+Marketing"
    },
    {
      title: "API Integration Guide for Developers",
      category: "Technology",
      excerpt: "Step-by-step tutorial on integrating MTN Service APIs into applications...",
      author: "Jennifer Adams",
      date: "Jan 28, 2025",
      readTime: "10 min read",
      image: "https://placehold.co/600x400/1E3A5F/FFFFFF?text=API+Integration"
    },
    {
      title: "Customer Success Stories: Digital Transformation",
      category: "News & Updates",
      excerpt: "How local businesses leveraged our platforms to achieve growth...",
      author: "Martin Opoku",
      date: "Jan 25, 2025",
      readTime: "6 min read",
      image: "https://placehold.co/600x400/FF6B2C/FFFFFF?text=Success+Stories"
    },
    {
      title: "Security Best Practices for Digital Transactions",
      category: "Digital Services",
      excerpt: "Essential security measures every business should implement...",
      author: "Sarah Johnson",
      date: "Jan 22, 2025",
      readTime: "7 min read",
      image: "https://placehold.co/600x400/1E3A5F/FFFFFF?text=Security"
    },
    {
      title: "The Rise of Mobile-First Business Solutions",
      category: "Technology",
      excerpt: "Why mobile optimization is no longer optional for modern businesses...",
      author: "Emmanuel Taylor",
      date: "Jan 20, 2025",
      readTime: "5 min read",
      image: "https://placehold.co/600x400/FF6B2C/FFFFFF?text=Mobile+First"
    },
    {
      title: "Quarterly Market Analysis: Q4 2024",
      category: "Business",
      excerpt: "In-depth analysis of market trends and business opportunities...",
      author: "Michael Carter",
      date: "Jan 15, 2025",
      readTime: "9 min read",
      image: "https://placehold.co/600x400/1E3A5F/FFFFFF?text=Market+Analysis"
    },
    {
      title: "Introducing Our New Developer Portal",
      category: "News & Updates",
      excerpt: "Explore the latest tools and resources for developers...",
      author: "Kwame Mensah",
      date: "Jan 12, 2025",
      readTime: "4 min read",
      image: "https://placehold.co/600x400/FF6B2C/FFFFFF?text=Developer+Portal"
    },
    {
      title: "Bulk SMS vs Push Notifications: Which to Choose?",
      category: "Digital Services",
      excerpt: "Comparing two popular communication channels for businesses...",
      author: "Ama Osei",
      date: "Jan 10, 2025",
      readTime: "6 min read",
      image: "https://placehold.co/600x400/1E3A5F/FFFFFF?text=Bulk+SMS"
    }
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <div className="pt-32 pb-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand-cream to-orange-50 py-20">
        <div className="max-w-8xl mx-auto px-6 lg:px-16 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-brand-orange text-sm font-semibold tracking-wider"
          >
            BLOG
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-5xl font-bold text-gray-900 mt-4 mb-6"
          >
            Insights & Updates
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-xl text-gray-600 max-w-2xl mx-auto"
          >
            Stay informed with the latest trends, tips, and news in digital communication and technology.
          </motion.p>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="py-12">
        <div className="max-w-8xl mx-auto px-6 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-wrap gap-4 justify-center"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-3 rounded-full transition ${
                  activeCategory === category
                    ? 'bg-brand-orange text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-brand-orange'
                }`}
              >
                {category}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-12">
        <div className="max-w-8xl mx-auto px-6 lg:px-16">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1 }
              }
            }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {blogPosts.map((post, index) => (
              <motion.article
                key={index}
                variants={fadeUp}
                whileHover={{ scale: 1.02, y: -8 }}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
              >
                <div className="relative">
                  <img 
                    src={post.image}
                    alt={post.title}
                    className="w-full aspect-video object-cover"
                  />
                  <span className="absolute top-4 left-4 bg-brand-orange text-white px-4 py-1 rounded-full text-xs">
                    {post.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 hover:text-brand-orange transition">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-blue to-brand-navy"></div>
                    <div>
                      <p className="text-sm font-semibold">{post.author}</p>
                      <p className="text-xs text-gray-500">{post.date} · {post.readTime}</p>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>

          {/* Pagination */}
          <div className="flex justify-center items-center gap-2 mt-16">
            <button className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:border-brand-orange transition">
              <ChevronLeft className="w-5 h-5" />
            </button>
            {[1, 2, 3, 4, 5].map((page) => (
              <button
                key={page}
                className={`w-10 h-10 rounded-lg border ${
                  page === 1
                    ? 'bg-brand-orange text-white border-brand-orange'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-brand-orange'
                } transition`}
              >
                {page}
              </button>
            ))}
            <button className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:border-brand-orange transition">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;